import { useSyncExternalStore } from "react";

/**
 * Caminho publicado por um módulo embutido (GEN-3497).
 *
 * Alguns módulos vivem em libs próprias e conhecem degraus que o host não tem como saber — a Base
 * Cadastral, por exemplo, sabe QUAL empresa está aberta ("Base Cadastral / Por Empresa / 57.319.738
 * LEANDRO VIEIRA DA SILVA"), enquanto o host só enxerga a rota `/base-cadastral/empresa`.
 *
 * Antes cada módulo resolvia isso desenhando a própria trilha dentro da página. O resultado é que o
 * caminho ficava num lugar diferente do resto do sistema, rolava junto com o conteúdo e sumia, e na
 * home do módulo repetia palavra por palavra o título logo abaixo. Agora o módulo PUBLICA os degraus
 * aqui e quem desenha é a faixa global — um lugar só, para todos os módulos.
 *
 * Store externo em vez de contexto React de propósito: quem publica é uma lib montada numa subárvore
 * distante da faixa, e um contexto exigiria envolver as duas na mesma árvore de provider — o que
 * ligaria a faixa ao ciclo de vida do módulo.
 */
export interface IModuleCrumb {
  label: string;
  /** Rota para onde o degrau leva. O último degrau não navega: é a página atual. */
  to?: string;
  /**
   * Ação de abrir o degrau, quando quem navega NÃO é o roteador do host.
   *
   * O Kanban 2.0 é montado num root React separado, com router próprio: um link do host trocaria a
   * URL sem que o roteador do módulo ficasse sabendo — a barra mudaria e a tela não. Módulos assim
   * publicam a ação em vez do endereço, e quem navega é o roteador que de fato governa a tela.
   */
  aoAbrir?: () => void;
}

let caminhoAtual: IModuleCrumb[] = [];
/**
 * Rota que estava aberta quando o módulo publicou. Guardada pelo HOST, não pelo módulo.
 *
 * Por que existe: o caminho publicado VENCE o que o host resolve pela rota, e este store é uma
 * variável de módulo — sobrevive a troca de tela. Se um dia a limpeza do módulo não rodar (versão
 * de lib que esquece o cleanup, erro que derruba a subárvore de um jeito atípico), a faixa passaria
 * a mostrar "Base Cadastral / …" em cima de /empresas, sem nada no código do host explicando por
 * quê. Com a rota gravada, o host descarta um caminho que não é da tela atual.
 */
let rotaDaPublicacao = "";
const ouvintes = new Set<() => void>();

/**
 * Chamado pelo módulo embutido a cada troca de tela. Passar lista vazia significa "não tenho
 * caminho próprio" — é o que o módulo faz ao ser desmontado, e devolve a palavra ao host.
 */
export const publicarCaminhoDoModulo = (itens: IModuleCrumb[]): void => {
  // Compara por conteúdo: o publicador manda um array novo a cada render, e notificar sem mudança
  // real re-renderizaria a faixa a cada render do módulo.
  //
  // A ROTA entra na comparação junto com os rótulos. Sem ela, duas telas diferentes com os mesmos
  // rótulos (dois quadros chamados "Certidões", cada um com sua página de Arquivos) eram tratadas
  // como a mesma publicação — a segunda era descartada e as closures da PRIMEIRA continuavam
  // valendo, então clicar no caminho levava ao quadro errado.
  const igual =
    rotaDaPublicacao === window.location.pathname &&
    caminhoAtual.length === itens.length &&
    caminhoAtual.every(
      (atual, i) =>
        atual.label === itens[i].label &&
        atual.to === itens[i].to &&
        // Só a PRESENÇA da ação entra na comparação, não a identidade: o publicador monta uma
        // closure nova a cada render, e comparar por referência faria a faixa se renderizar sem
        // nenhuma mudança real.
        !!atual.aoAbrir === !!itens[i].aoAbrir,
    );
  if (igual) return;

  caminhoAtual = itens;
  rotaDaPublicacao = itens.length ? window.location.pathname : "";
  ouvintes.forEach((notificar) => notificar());
};

const assinar = (notificar: () => void): (() => void) => {
  ouvintes.add(notificar);
  return () => {
    ouvintes.delete(notificar);
  };
};

/** Snapshot do store: a lista publicada, como está. Referência estável entre leituras. */
const ler = (): IModuleCrumb[] => caminhoAtual;

/**
 * A rota em que o caminho atual foi publicado. Quem compara com a tela aberta é a faixa
 * (`Trail`), com o `pathname` do roteador — de propósito: se a comparação morasse no snapshot do
 * store, ele mudaria sem a store notificar, o que quebra a garantia do `useSyncExternalStore`.
 */
export const rotaDoCaminhoPublicado = (): string => rotaDaPublicacao;

/**
 * O caminho publicado pelo módulo aberto, ou lista vazia quando não há módulo embutido na tela —
 * caso em que a faixa usa o caminho que ela mesma resolve a partir da rota.
 */
export const useCaminhoDoModulo = (): IModuleCrumb[] => useSyncExternalStore(assinar, ler, ler);
